import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Check, Key, Shield, Terminal } from "lucide-react";
import { trackApiKeyCreated } from "../lib/analytics";
import { FORGE_NPM_PACKAGE_NAME } from "@/constants";

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<{ success: boolean; key?: string }>;
}

export const CreateKeyModal: React.FC<CreateKeyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isCommandCopied, setIsCommandCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await onSubmit(name);
      if (result.success && result.key) {
        setCreatedKey(result.key);
        // Track API key creation as a conversion event
        trackApiKeyCreated();
      }
    } catch (error) {
      console.error("Error creating key:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName("");
    setCreatedKey(null);
    setIsCopied(false);
    setIsCommandCopied(false);
    setIsSubmitting(false);
    onClose();
  };

  const copyToClipboard = async () => {
    if (createdKey) {
      try {
        await navigator.clipboard.writeText(createdKey);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 3000);
      } catch (error) {
        console.error("Failed to copy:", error);
      }
    }
  };

  const copyCommand = async () => {
    if (createdKey) {
      try {
        await navigator.clipboard.writeText(
          `FORGE_KEY=${createdKey} npx ${FORGE_NPM_PACKAGE_NAME}@latest`,
        );
        setIsCommandCopied(true);
        setTimeout(() => setIsCommandCopied(false), 3000);
      } catch (error) {
        console.error("Failed to copy command:", error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && name.trim() && !isSubmitting && !createdKey) {
      handleSubmit();
    }
  };

  if (createdKey) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg w-full mx-4 p-0 gap-0">
          {/* Apple-style clean header */}
          <DialogHeader className="text-center px-8 pt-8 pb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <Check
                className="h-8 w-8 text-green-600 dark:text-green-400"
                strokeWidth={2}
              />
            </div>
            <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Your Access Key is Ready
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Copy your key now and store it safely. For security, you won't be
              able to view it again.
            </DialogDescription>
          </DialogHeader>

          <div className="px-8 pb-8 space-y-6">
            {/* Key display section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Your Access Key
              </Label>
              <div className="relative group">
                <div
                  className="w-full p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => {
                    const selection = window.getSelection();
                    const range = document.createRange();
                    const keyElement = document.getElementById("api-key-text");
                    if (keyElement && selection) {
                      range.selectNodeContents(keyElement);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                  }}
                >
                  <code
                    id="api-key-text"
                    className="block w-full text-xs font-mono break-all text-gray-700 dark:text-gray-300"
                  >
                    {createdKey}
                  </code>
                </div>

                <Button
                  onClick={copyToClipboard}
                  variant={"secondary"}
                  className="absolute top-2 right-2 h-8 px-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  size="sm"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Security notice - Apple-style subtle warning */}
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                    Keep Your Key Secure
                  </h4>
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    Store this key in a secure location like a password manager.
                    Anyone with this key can access your account.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick start section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Terminal className="h-4 w-4" />
                Quick Start
              </Label>
              <div className="relative group">
                <div className="w-full p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <code className="block w-full text-xs sm:text-sm md:text-base lg:text-lg font-mono break-all text-gray-700 dark:text-gray-300">
                    FORGE_KEY={createdKey} npx {FORGE_NPM_PACKAGE_NAME}@latest
                  </code>
                </div>

                <Button
                  onClick={copyCommand}
                  variant="outline"
                  className="absolute top-2 right-2 h-8 px-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  size="sm"
                >
                  {isCommandCopied ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="px-8 pb-8 pt-0">
            <Button onClick={handleClose} className="w-full" size="lg">
              I've Saved My Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-full mx-4 p-0 gap-0">
        <DialogHeader className="text-center px-8 pt-8 pb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
            <Key
              className="h-8 w-8 text-blue-600 dark:text-blue-400"
              strokeWidth={2}
            />
          </div>
          <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Create Access Key
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Give your key a name to help you identify it later.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 pb-6">
          <div className="space-y-3">
            <Label
              htmlFor="key-name"
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Key Name
            </Label>
            <Input
              id="key-name"
              type="text"
              placeholder="e.g., Production App, Development"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              autoFocus
              required
              className="h-11"
            />
          </div>
        </div>

        <DialogFooter className="px-8 pb-8 pt-0">
          <div className="flex flex-col gap-3 w-full">
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || isSubmitting}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  Creating Key...
                </>
              ) : (
                "Create Access Key"
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-full"
              size="lg"
            >
              Cancel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
